import type { MediaAsset } from '../types/media';

const trimSlash = (s: string) => s.replace(/\/$/, '');
const isHttpUrl = (s: string | undefined | null) => !!s && /^https?:\/\//i.test(s);

const normalizeStoragePath = (rawPath: string) => {
  let normalized = rawPath.trim().replace(/^\/+/, '').replace(/\\/g, '/');

  // Strip "uploads/" or "media/" prefixes repeatedly until none remain
  while (/^(?:uploads|media)\//i.test(normalized)) {
    normalized = normalized.replace(/^(?:uploads|media)\//i, '');
  }

  return normalized;
};

const getPublicBase = () => {
  const base = import.meta.env.VITE_MEDIA_PUBLIC_BASE_URL as string | undefined;
  return base ? trimSlash(base) : undefined;
};

const getApiBase = () => {
  const base = (import.meta.env.VITE_MEDIA_API_BASE_URL as string | undefined) ?? '/api';
  return trimSlash(base);
};

export const resolveMediaUrl = (url?: string | null, path?: string | null): string => {
  // If absolute URL provided, use it
  if (isHttpUrl(url)) return url as string;

  const publicBase = getPublicBase();

  // If a site-relative URL like /media/..., prefix with public base if provided
  if (url && url.startsWith('/')) {
    if (!publicBase) {
      return url;
    }
    const normalizedFromUrl = normalizeStoragePath(url);
    return `${publicBase}/${normalizedFromUrl}`;
  }

  // If we only have a storage path (relative path inside uploads), build it
  if (path && path.length > 0) {
    const normalizedPath = normalizeStoragePath(path);

    if (publicBase) return `${publicBase}/${normalizedPath}`;
    // Fallback: derive from api base if it points to same origin proxy
    const apiBase = getApiBase();
    // Try to strip trailing /api to get origin of media server
    const originGuess = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
    const prefix = originGuess.length > 0 ? originGuess : '';
    return `${prefix}/media/${normalizeStoragePath(path)}`;
  }

  // Nothing better known
  return url ?? '';
};

export const getAssetUrls = (asset?: MediaAsset | null) => {
  if (!asset) {
    return { mediaUrl: '', thumbnailUrl: '' };
  }
  return {
    mediaUrl: resolveMediaUrl(asset.mediaUrl, asset.storagePath ?? undefined),
    thumbnailUrl: resolveMediaUrl(asset.thumbnailUrl, asset.thumbnailPath ?? undefined),
  };
};

